import { useEffect, useState } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import toast from 'react-hot-toast';
import { Mail, Building2, Check } from 'lucide-react';
import { db } from '@/lib/firebase';
import { useAuth } from '@/hooks/useAuth';
import { callAcceptInvite } from '@/lib/functions';
import { Logo } from '@/components/Logo';
import { Loader } from '@/components/ui/loader';
import { Button } from '@/components/ui/button';
import type { Invitation } from '@/types/models';

export default function AcceptInvite() {
  const { inviteId } = useParams();
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [invite, setInvite] = useState<Invitation | null>(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    if (!inviteId) return;
    (async () => {
      const snap = await getDoc(doc(db, 'invitations', inviteId));
      if (snap.exists()) setInvite({ id: snap.id, ...snap.data() } as Invitation);
      setLoading(false);
    })();
  }, [inviteId]);

  if (!user) {
    return <Navigate to={`/login?invite=${inviteId}`} replace />;
  }
  if (loading) return <Loader fullscreen label="Chargement de l'invitation…" />;

  if (!invite) {
    return (
      <Centered>
        <h2 className="font-display text-2xl">Invitation introuvable</h2>
        <p className="mt-2 text-sm text-ink-500">Ce lien est invalide ou a expiré.</p>
      </Centered>
    );
  }

  if (invite.status !== 'pending') {
    return (
      <Centered>
        <h2 className="font-display text-2xl">Invitation {invite.status === 'accepted' ? 'déjà acceptée' : invite.status}</h2>
      </Centered>
    );
  }

  if (invite.email.toLowerCase() !== user.email?.toLowerCase()) {
    return (
      <Centered>
        <h2 className="font-display text-2xl">Email ne correspond pas</h2>
        <p className="mt-2 text-sm text-ink-500">
          Cette invitation a été émise pour <strong>{invite.email}</strong>, mais vous êtes connecté en tant que <strong>{user.email}</strong>.
        </p>
      </Centered>
    );
  }

  const accept = async () => {
    setAccepting(true);
    try {
      await callAcceptInvite({ inviteId: invite.id });
      await refresh();
      toast.success('Invitation acceptée. Bienvenue !');
      nav('/', { replace: true });
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setAccepting(false);
    }
  };

  return (
    <Centered>
      <div className="flex flex-col items-center text-center">
        <Logo variant="icon" className="h-10 w-10" />
        <h2 className="mt-5 font-display text-2xl text-ink-900 dark:text-white">Vous êtes invité·e</h2>
        <p className="mt-2 text-sm text-ink-500">à rejoindre l'organisation</p>

        <div className="mt-5 flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-5 py-3 shadow-soft dark:border-border dark:bg-card">
          <Building2 className="h-5 w-5 text-brand-500" />
          <div className="text-left">
            <div className="font-medium text-ink-900 dark:text-white">{invite.orgName}</div>
            <div className="text-xs text-ink-500">en tant que <code>{invite.role}</code></div>
          </div>
        </div>

        <div className="mt-3 flex items-center gap-2 text-xs text-ink-500">
          <Mail className="h-3.5 w-3.5" /> {invite.email}
        </div>

        <Button onClick={accept} disabled={accepting} className="mt-8">
          <Check className="h-4 w-4" /> Accepter l'invitation
        </Button>
      </div>
    </Centered>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="grid h-full place-items-center bg-slate-50 px-6 dark:bg-background">
      <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-8 shadow-soft dark:border-border dark:bg-card">{children}</div>
    </div>
  );
}
